// ===== Ad-network converter core =====
//
// Unity Ads still runs the JS port of playworks_adnetwork_converter.bat
// (decoded PowerShell) at the bottom of this file. The other seven networks
// do not: each was rebuilt from evidence and returns from convert() before
// that shared path ever runs.
//
//   googleads · applovin · mintegral   타사 정식 Luna 빌드를 자사 unityAds
//   moloco                             빌드와 블록 단위로 대조해 도출
//   ironsource                         해당 네트워크에 송출된 소재에서 도출
//   meta                               Meta 공식 사양에서 역산
//   smadex                             Smadex Playables 규격 시트에서 도출
//
// 근거는 각 convertX() 위 주석에 있고, 대조에 쓴 실물은 DEBUG_*/ 에 있습니다.
// Pure functions only — no DOM, no I/O. UI lives in convert.js.

import { GOOGLE_WRAPPER_SCRIPTS } from './google-wrapper.js';
import { APPLOVIN_BRIDGE } from './applovin-bridge.js';
import { MINTEGRAL_LIFECYCLE, MINTEGRAL_CTA, MINTEGRAL_AD_DATA } from './mintegral-blocks.js';
import { MOLOCO_TRAMPOLINE } from './moloco-trampoline.js';

// Networks. Unity Ads first — it's the default build target.
export const NETWORKS = [
  { key: 'unityads',   name: 'Unity Ads',  suffix: '_UnityAds'   },
  { key: 'googleads',  name: 'Google Ads', suffix: '_GoogleAds'  },
  { key: 'mintegral',  name: 'Mintegral',  suffix: '_Mintegral'  },
  { key: 'ironsource', name: 'IronSource', suffix: '_IronSource' },
  { key: 'applovin',   name: 'AppLovin',   suffix: '_AppLovin'   },
  { key: 'meta',       name: 'Meta',       suffix: '_Meta'       },
  { key: 'moloco',     name: 'Moloco',     suffix: '_Moloco'     },
  { key: 'smadex',     name: 'Smadex',     suffix: '_Smadex'     },
];

// Sources are usually named after the network they were built for
// ("NNS_P_IN_000_Undress_unityAds.html"). That tag is wrong the moment we
// re-target the creative, and outName appends the new one, so drop it.
// Separators inside a two-word name are optional: "_unityAds", "_unity_ads"
// and " Unity Ads" all match.
const NET_SUFFIX_RE = new RegExp(
  '[ _-]+(?:' + NETWORKS.map(n => n.name.replace(/ /g, '[ _-]?')).join('|') + ')$',
  'i'
);

export function stripNetworkSuffix(stem) {
  return stem.replace(NET_SUFFIX_RE, '') || stem;
}

// Output filename for a converted creative: "stem_Meta.html".
export function outName(baseName, network, ext) {
  const stem = stripNetworkSuffix(baseName.replace(/\.html?$/i, ''));
  return stem + network.suffix + '.' + ext;
}

// ---------------------------------------------------------------------------
// base122 decode  (port of the `Dec` PowerShell function in :check_base64)
// Returns the decoded bytes. base122 chars are all <= 0x7FF so UTF-16 code
// units map 1:1 to PowerShell's [int]$s[$i] — charCodeAt matches exactly.
// ---------------------------------------------------------------------------
const B122_ILLEGALS = [0, 10, 13, 34, 38, 92];

function base122DecodeBytes(s) {
  const len = s.length;
  const out = new Uint8Array(Math.floor(len * 1.75) + 8);
  let n = 0, cur = 0, bits = 0;

  const emit7 = (val) => {
    const o = (val << 1) & 0xFF;     // 7-bit value packed into the high bits
    cur = cur | (o >> bits);
    bits += 7;
    if (bits >= 8) {
      out[n++] = cur & 0xFF;
      bits -= 8;
      cur = (o << (7 - bits)) & 0xFF;
    }
  };

  for (let i = 0; i < len; i++) {
    const cc = s.charCodeAt(i);
    if (cc > 127) {
      const idx = (cc >> 8) & 7;
      if (idx !== 7) emit7(B122_ILLEGALS[idx]);
      emit7(cc & 127);
    } else {
      emit7(cc);
    }
  }
  return out.subarray(0, n);
}

function bytesToBase64(bytes) {
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
}

// base122 string → standard base64 string
function b122ToBase64(s) {
  return bytesToBase64(base122DecodeBytes(s));
}

// ---------------------------------------------------------------------------
// HTML entity unescape for a base122 payload read out of an attribute value.
// The browser resolves entities when it hands back `el.dataset.src122`, so a
// payload lifted from an attribute into JS has to be unescaped by hand or it
// decodes to garbage (metaplayable무한로딩분석.md §4-3).
// Only these three can occur: base122 emits no '"' and no raw '&', so the
// writer has nothing else to escape — and every '&' here opens an entity.
// ---------------------------------------------------------------------------
function unescapeAttrPayload(s) {
  return s.indexOf('&') < 0 ? s
    : s.replace(/&(lt|gt|amp);/g, (_m, e) => (e === 'lt' ? '<' : e === 'gt' ? '>' : '&'));
}

// ===========================================================================
// Meta (Facebook) 전용 파이프라인
//
// Meta의 플레이어블 업로더는 업로드된 HTML에서 C0 제어문자(TAB 제외)를
// 제거합니다. Luna 빌드는 이미지도 게임 에셋도 base122로 인코딩하는데,
// base122 출력의 약 22%가 제어문자라 업로드만으로 페이로드가 손상됩니다.
//
// 처음에는 속성값만 필터링된다고 보고 페이로드를 <script> 문자열 리터럴로
// 옮겼습니다(2026-08-18). 그 소재에서는 통했지만, 2026-08-21 소재에서는
// 스크립트 안 브로틀리 페이로드까지 손상됐습니다 — 깨진 입력을 받은 JS
// 브로틀리 디코더가 무한루프에 빠져 메인 스레드가 통째로 멈췄고(진단
// 오버레이의 시계가 t=0.1에서 정지), 로딩바만 계속 도는 화면이 됐습니다.
//
// 그래서 저장 위치를 옮기는 대신 **인코딩을 바꿉니다**. base64는 문자셋이
// [A-Za-z0-9+/=]뿐이라 지울 제어문자가 아예 없습니다. 대가는 크기 — 같은
// 데이터가 base122 대비 ~17% 커지므로 [8]에서 5MB 한도를 검사합니다.
// 전체 조사 기록: metaplayable무한로딩분석.md
// ===========================================================================

const META_SIZE_LIMIT = 5 * 1024 * 1024;

// Smadex 규격: "Ensure the file is less than 5MB." — 이하가 아니라 미만입니다.
const SMADEX_SIZE_LIMIT = 5 * 1024 * 1024;

// decompressArrayBuffer("…", true) / decompressString("…", true)
// 두 번째 인자가 assetToArrayBuffer에서 _base122ToArrayBuffer /
// _base64ToArrayBuffer 를 고르는 스위치입니다.
const RX_DECOMPRESS_B122 = /(decompress(?:ArrayBuffer|String)\(\s*")([^"]*)("\s*,\s*)true(\s*\))/g;

// data-src122="…" data-mime="…" (순서 무관) → src="data:…;base64,…"
const RX_SRC122_PAIR =
  /data-src122="([^"]*)"\s+data-mime="([^"]*)"|data-mime="([^"]*)"\s+data-src122="([^"]*)"/g;

// Luna가 가끔 파일 끝을 `</body>\n</html></head></html>`로 내보냅니다.
// 크롬은 무시하지만 업로더 파서는 보장이 없으므로 정리합니다.
function cleanTrailingTags(content) {
  const i = content.lastIndexOf('</body>');
  if (i < 0) return { content, removed: 0 };
  const cut = i + '</body>'.length;
  const tail = content.slice(cut);
  if (!/^\s*(?:<\/(?:html|head|body)>\s*)+$/i.test(tail)) return { content, removed: 0 };
  const tags = tail.match(/<\/(?:html|head|body)>/gi) || [];
  if (tags.length <= 1) return { content, removed: 0 };
  return { content: content.slice(0, cut) + '\n</html>\n', removed: tags.length - 1 };
}

// ---------------------------------------------------------------------------
// [1] Remove the devtools/console override <script> block.
//
// Anchored on the token, not on the script's opening shape. The .bat's pattern
// (`<script>(()=>{ … token … }())</script>` with lazy middles) latched onto the
// FIRST `<script>(()=>{` in the document — in real Luna builds that is the
// touch/pointer-event polyfill — and then ran forward THROUGH intervening
// </script> boundaries to reach the token: 5,830 chars deleted where the real
// block is 1,268, taking the polyfill and the engine bootstrap with it.
// Scoping the cut to the single <script> element that contains the token
// cannot over-reach.
// ---------------------------------------------------------------------------
const DEVTOOLS_TOKEN = 'insertYourRemoteDebuggingTokenHere';

function removeDevtoolsBlock(content) {
  let removed = 0;
  for (;;) {
    const tok = content.indexOf(DEVTOOLS_TOKEN);
    if (tok < 0) break;
    const start = content.lastIndexOf('<script', tok);
    const close = content.indexOf('</script>', tok);
    // Only cut a well-formed <script>…</script> that really encloses the token.
    if (start < 0 || close < 0) break;
    content = content.slice(0, start) + content.slice(close + '</script>'.length);
    removed++;
  }
  return { content, removed };
}

// ---------------------------------------------------------------------------
// [1b] Luna ships two dev-only script injectors, both built the same way: a
// flag-gated `if` that creates a <script> and points it at an absolute URL.
//
//   stats.js   FPS overlay, behind `?fps`      mrdoob.github.io
//   spectorjs  WebGL capture, behind `?spector` cdn.jsdelivr.net
//
// Neither fires on a real impression, but the URLs sit in the file and creative
// checks scan statically for external references (Meta and Moloco both forbid
// them). Blanking the URL is enough and cannot break parsing; cutting the
// surrounding minified `if` would mean brace-matching against variable names
// that change every build.
// ---------------------------------------------------------------------------
const RX_DEV_SCRIPT_URL =
  /"https?:\/\/[^"]*\b(?:stats(?:\.min)?|spector(?:\.bundle)?(?:\.min)?)\.js"/g;

// Resource loads only. `src=`/`href=` catches both the HTML attribute and the
// minified `e.src="…"` assignment. Store links live in iosLink/androidLink and
// are data the CTA reads, not fetches, so they stay out of this.
const RX_EXTERNAL_REF = /\b(?:src|href)\s*=\s*"https?:\/\/[^"]*"/g;

// Luna's playground asset overrides point a handful of asset ids at
// assets.lunalabs.io. They are a preview-time feature — the inlined assets are
// what ship — but the URLs are remote references sitting in the file, and left
// in place the runtime refetches those textures from the CDN. The object is a
// flat id -> URL map, so a brace-to-brace match is exact.
const RX_ASSET_OVERRIDES = /window\.playgroundAssetOverrides\s*=\s*\{[^}]*\}/g;

function stripAssetOverrides(content) {
  let count = 0;
  const out = content.replace(RX_ASSET_OVERRIDES, (m) => {
    if (/"\s*:\s*"https?:/.test(m)) count++;
    return 'window.playgroundAssetOverrides = {}';
  });
  return { content: out, count };
}

function stripDevScriptUrls(content) {
  const hosts = [];
  const out = content.replace(RX_DEV_SCRIPT_URL, (m) => {
    const h = /https?:\/\/([^/"]+)/.exec(m);
    if (h && !hosts.includes(h[1])) hosts.push(h[1]);
    return '""';
  });
  return { content: out, count: hosts.length, hosts };
}

function findExternalRefs(content) {
  return [...new Set(content.match(RX_EXTERNAL_REF) || [])];
}

// ---------------------------------------------------------------------------
// Luna's preload screen is left alone.
//
// It used to be cut when its app-icon slot still held Luna's 1x1 transparent
// placeholder, on the grounds that CSS blows that 1px up to 15vmax and Google
// Ads review rejects the resulting frame. Two things were wrong with that.
// Google never reached this code — convert() dispatches googleads (and the five
// other rebuilt targets) to its own function well before this shared path, so
// the only network the strip ever ran on was Unity Ads, which the rationale
// says nothing about. And the genuine Luna exports keep the overlay either way:
// DEBUG_GOOGLE/googleAds.html ships the same 92-char placeholder as its source,
// while the AppLovin exports carry real icons. Cutting it made Unity Ads the
// one target that diverged from every reference build for a reason belonging to
// a different network.
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// [2] Escape backticks inside the decompressArrayBuffer("…")/decompressString("…")
// payload. base122 never emits '"' (34 is illegal), so [^"]* is exactly the
// payload and the escape cannot leak past it.
//
// This replaces the .bat's index-scanning FixBackticks, which searched for a
// literal end marker (`", true)` / `")`) and — when the call had a different
// argument list, as real Luna builds do with decompressString("…", true) — ran
// on to the next match anywhere in the file, rewriting backticks in unrelated
// code (template literals in the game bundle) along the way.
// ---------------------------------------------------------------------------
const RX_DECOMPRESS_PAYLOAD = /(decompress(?:ArrayBuffer|String)\(\s*")([^"]*)(")/g;

function fixBackticks(content) {
  let count = 0;
  const out = content.replace(RX_DECOMPRESS_PAYLOAD, (m, pre, payload, post) => {
    if (payload.indexOf('`') < 0) return m;
    count++;
    return pre + payload.replace(/`/g, '\\x60') + post;
  });
  return { content: out, count };
}

function setTargetPlatform(content, key) {
  return content.replace(/targetPlatform:"[^"]*"/g, `targetPlatform:"${key}"`);
}

// ===========================================================================
// Google Ads 플레이어블 파이프라인
//
// 이 파이프라인은 PAW의 다른 네트워크와 코드를 공유하지 않습니다. 타사가
// Luna로 google 타겟을 직접 빌드한 파일 3개(googleAds/googleAds2/googleAds3)를
// 자사 unityAds 빌드와 대조해 도출했고, 각 단계가 "정식 빌드가 실제로 그렇게
// 되어 있다"는 근거를 갖습니다.
//
// 검증 방법 — 불변 블록:
//   서로 다른 게임의 정식 google 빌드 두 개에서 모든 <script>의 md5를 떠서
//   교집합을 구하면 16개가 남습니다. 게임과 무관한 순수 플랫폼 구조입니다.
//   변환 결과가 이 16개를 전부 포함하면 구조적으로 정식 빌드와 동일합니다.
//   (이 방법이 실제로 오류를 하나 잡았습니다: CTA에 pi.logCta()를 남기면
//    15/16이 됩니다. 정식 빌드 두 개가 바이트까지 똑같이 그걸 뺍니다.)
//
// unityAds → google 에서 실제로 달라지는 것은 다음 8가지뿐입니다.
// ===========================================================================

// ---------------------------------------------------------------------------
// 토큰을 품은 <script> 엘리먼트 하나를 정확히 집어냅니다. 최소화된 Luna
// 빌드에서는 변수명이 매 빌드 바뀌므로 코드 모양이 아니라 API 이름에
// 앵커를 겁니다. 토큰과 여는 태그 사이에 </script>가 있으면 그 <script>는
// 토큰을 감싼 태그가 아니므로 null을 돌려줍니다.
// ---------------------------------------------------------------------------
function findScriptWith(content, token) {
  const tok = content.indexOf(token);
  if (tok < 0) return null;
  const start = content.lastIndexOf('<script', tok);
  if (start < 0) return null;
  const open = content.indexOf('>', start);
  if (open < 0 || open > tok) return null;
  if (content.slice(start, tok).indexOf('</script') >= 0) return null;
  const close = content.indexOf('</script>', tok);
  if (close < 0) return null;
  return { start, bodyStart: open + 1, bodyEnd: close, end: close + '</script>'.length };
}

// 토큰을 품은 <script>의 **본문만** 갈아끼웁니다(태그와 속성은 유지).
function replaceScriptBody(content, token, body) {
  const s = findScriptWith(content, token);
  if (!s) return null;
  return content.slice(0, s.bodyStart) + body + content.slice(s.bodyEnd);
}

// 토큰을 품은 <script> 엘리먼트를 통째로 들어냅니다.
function cutScriptWith(content, token) {
  const s = findScriptWith(content, token);
  if (!s) return null;
  return content.slice(0, s.start) + content.slice(s.end);
}

// [G1] Luna가 unityAds 빌드의 파일 끝을
// `</body>\n</html></head></html>`로 내보냅니다. 정식 google 빌드는 깨끗합니다.
function googleCleanTail(content) {
  const i = content.lastIndexOf('</body>');
  if (i < 0) return { content, ok: false };
  const tail = content.slice(i + '</body>'.length);
  if (!/^\s*(?:<\/(?:html|head|body)>\s*)*$/i.test(tail)) return { content, ok: false };
  // 앞쪽 공백까지 걷어내야 재변환해도 결과가 같습니다. 그러지 않으면 이미
  // 변환된 파일을 다시 넣을 때마다 </body> 앞에 개행이 하나씩 쌓입니다.
  return { content: content.slice(0, i).replace(/\s+$/, '') + '\n</body></html>', ok: true };
}

// [G2] `<meta name="ad.orientation">`는 packageConfig.orientation을 따라갑니다.
// googleAds는 unspecified → "portrait,landscape", googleAds3는 portrait →
// "portrait"였습니다. 알 수 없는 값이면 양쪽을 허용하는 쪽으로 둡니다.
function googleOrientation(content) {
  const m = content.match(/\borientation:\s*"([^"]*)"/);
  const v = m ? m[1].toLowerCase() : '';
  if (v === 'portrait' || v === 'landscape') return v;
  return 'portrait,landscape';
}

// ===========================================================================
// 두 번째 InstallFullGame
//
// 2026-10 이후의 Luna Unity Ads 내보내기는 분석 블록(window.callAnalyticsEvent
// → uads.sendPlayableAnalytic)을 하나 더 붙이고, 그 안에서 luna:build 에
// InstallFullGame 을 다시 대입합니다(mraid.open / window.open). 등록 순서상
// 그 대입이 최종 승자라, 첫 CTA 블록만 바꾼 파이프라인(Google · Mintegral ·
// Moloco · Meta)에서는 CTA가 여전히 스토어를 직접 엽니다 — PlayTurbo 의
// "CTA Call Method" 검사가 여기서 떨어졌습니다. 참조 빌드들은 이 블록이 없는
// 구버전 내보내기라 지울 근거도 남길 근거도 없습니다. 분석 전송 자체는
// 그대로 두고(해당 호스트 객체는 다른 네트워크에서 undefined 라 no-op),
// 뒤에 오는 InstallFullGame 대입만 첫 정의(각 네트워크가 방금 넣은 CTA)와
// 같은 함수로 맞춥니다. 브릿지를 </body> 앞에 덧붙여 최종 승자가 되는
// AppLovin · Smadex 와 mraid 를 그대로 쓰는 Unity Ads · IronSource 는 해당
// 없습니다.
//
// 함수 끝은 중괄호 짝으로 찾습니다 — 최소화된 Luna CTA 함수 안의 문자열에는
// 중괄호가 없습니다.
// ===========================================================================
const CTA_NAME = 'Luna.Unity.Playable.InstallFullGame';

function ctaAssignments(content) {
  const out = [];
  let from = 0;
  for (;;) {
    const at = content.indexOf(CTA_NAME + '=function', from);
    if (at < 0) break;
    const brace = content.indexOf('{', at);
    if (brace < 0) break;
    let depth = 0, end = -1;
    for (let i = brace; i < content.length; i++) {
      const c = content[i];
      if (c === '{') depth++;
      else if (c === '}' && --depth === 0) { end = i; break; }
    }
    if (end < 0) break;
    out.push({ start: at, end: end + 1, fn: content.slice(at + CTA_NAME.length + 1, end + 1) });
    from = end + 1;
  }
  return out;
}

// 첫 정의의 함수를 뒤의 모든 정의에 복사합니다. 바뀐 개수를 돌려줍니다.
function alignCtaAssignments(content) {
  const defs = ctaAssignments(content);
  if (defs.length < 2) return { content, count: 0 };
  const fn = defs[0].fn;
  let count = 0;
  for (let i = defs.length - 1; i >= 1; i--) {
    if (defs[i].fn === fn) continue;
    content = content.slice(0, defs[i].start) + CTA_NAME + '=' + fn + content.slice(defs[i].end);
    count++;
  }
  return { content, count };
}

const CTA_ALIGN_LABEL = (n) => `분석 블록의 InstallFullGame 재정의 ${n}개를 같은 CTA로 맞춤`;

// [G3] CTA. 크리에이티브가 스토어 URL을 직접 열지 않고, 목적지는 Google이
// 주입합니다. 정식 빌드 두 개에서 바이트가 동일한 블록입니다(md5 1b88b512).
const GOOGLE_CTA_BODY =
  'window.addEventListener("luna:build",(()=>{Bridge.ready((()=>{' +
  'Luna.Unity.Playable.InstallFullGame=function(){window.ExitApi.exit()}}))}))';

// [G4] 생명주기. Google 컨테이너에 mraid는 없습니다. 원본 블록에도
// `typeof mraid === "undefined"` 폴백이 있어 남겨도 동작하지만, 정식 빌드는
// 블록 자체를 이것으로 갈아끼웁니다(md5 2099a725). devicePixelRatio=2는
// 캔버스 렌더 해상도를 올립니다.
const GOOGLE_LIFECYCLE_BODY =
  'window.addEventListener("DOMContentLoaded",(()=>{window.devicePixelRatio=2})),' +
  'window.addEventListener("luna:build",(function(){window.pi.logLoaded(),' +
  'window.dispatchEvent(new Event("luna:start"))}))';

// [G8] 이미지만 base64 data URI로. **압축 번들(scripts/jsons/blobs/sounds)은
// 건드리지 않습니다** — 정식 빌드 2/2가 base122를 그대로 유지합니다. 바꾸면
// 같은 데이터가 ~28만 바이트 커지기만 합니다.
//
// 원본은 <img data-src122> 상태로 실려 나가고 런타임에 Blob·objectURL로
// 되살아납니다. 정식 google 빌드는 3/3이 평범한 data URI입니다.
// base122는 원본 대비 1.143배, base64는 1.333배로 담깁니다.
function googleImagesToDataUri(content) {
  let count = 0;
  const next = content.replace(RX_SRC122_PAIR, (_m, srcA, mimeA, mimeB, srcB) => {
    const raw = srcA !== undefined ? srcA : srcB;
    const mime = unescapeAttrPayload(srcA !== undefined ? mimeA : mimeB);
    count++;
    return `src="data:${mime};base64,${b122ToBase64(unescapeAttrPayload(raw))}"`;
  });
  return { content: next, count, leftover: (next.match(/data-src122="/g) || []).length };
}

// 디코딩 드라이버 <script>는 그대로 둡니다. 정식 빌드도 그대로 두고(md5
// 21c09314), querySelectorAll("[data-src122]")가 빈 결과를 돌려주므로
// _decode122Promise가 즉시 resolve됩니다.

// ---------------------------------------------------------------------------
// Google Ads 변환 — 위 8단계만 적용합니다. 다른 네트워크의 공통 단계
// (devtools 제거 · stats.js · 프리로더 · backtick)는 타지 않습니다. 정식
// 빌드가 그것들을 그대로 갖고 있으므로, 건드리면 오히려 구조가 달라집니다.
// ---------------------------------------------------------------------------
function convertGoogleAds(html) {
  const log = [];
  let content = html;
  const step = (n, label, ok = true) => log.push({ step: String(n), label, ok });

  // [1] 파일 끝 닫는 태그 정리
  const tail = googleCleanTail(content);
  content = tail.content;
  step(1, tail.ok ? '파일 끝 닫는 태그 정리' : '⚠ </body>를 찾지 못해 태그 정리 건너뜀', tail.ok);

  // [2] Google 래퍼(ExitApi + secureChannel) + ad.orientation 삽입
  const orient = googleOrientation(content);
  const inject = `<meta name="ad.orientation" content="${orient}">` + GOOGLE_WRAPPER_SCRIPTS;
  if (content.indexOf('ExitApi.exit') >= 0) {
    step(2, 'Google 래퍼: 이미 존재 (스킵)');
  } else {
    const vp = content.match(/<meta\s+name="viewport"[^>]*>/i);
    if (vp) {
      content = content.replace(vp[0], () => vp[0] + inject);
      step(2, `Google 래퍼(ExitApi + secureChannel) 삽입 · ad.orientation="${orient}"`);
    } else if (content.indexOf('</head>') >= 0) {
      content = content.replace('</head>', () => inject + '</head>');
      step(2, `Google 래퍼 삽입(viewport meta 없어 </head> 앞) · ad.orientation="${orient}"`);
    } else {
      step(2, '⚠ <head>를 찾지 못해 Google 래퍼 미삽입', false);
    }
  }

  // [3] CTA → ExitApi.exit()
  const cta = replaceScriptBody(content, 'Luna.Unity.Playable.InstallFullGame', GOOGLE_CTA_BODY);
  if (cta) { content = cta; step(3, 'CTA → ExitApi.exit()'); }
  else step(3, '⚠ InstallFullGame 스크립트를 찾지 못함', false);
  const ctaAlign = alignCtaAssignments(content); content = ctaAlign.content;
  if (ctaAlign.count) step('3b', CTA_ALIGN_LABEL(ctaAlign.count));

  // [4] mraid 생명주기 → 즉시 시작 + devicePixelRatio=2
  const life = replaceScriptBody(content, 'mraid.isViewable()', GOOGLE_LIFECYCLE_BODY);
  if (life) { content = life; step(4, 'mraid 생명주기 → 즉시 luna:start + devicePixelRatio=2'); }
  else step(4, 'mraid 생명주기 블록 없음 (건너뜀)');

  // [5] mraid 광고 메타 주입 블록 제거
  let cut = 0;
  for (const token of ['mraid.getMraidAdData', 'window.pi.originalLogEvent']) {
    const next = cutScriptWith(content, token);
    if (next) { content = next; cut++; }
  }
  step(5, cut ? `mraid 광고 메타 주입 블록 ${cut}개 제거` : 'mraid 메타 주입 블록 없음 (건너뜀)');

  // [6] 외부 에셋 오버라이드 제거 — 남겨두면 런타임에 lunalabs CDN에서
  //     텍스처를 다시 받습니다. 플레이어블은 자기완결이어야 합니다.
  const ao = stripAssetOverrides(content); content = ao.content;
  step(6, ao.count ? `외부 에셋 오버라이드 ${ao.count}블록 비움` : '외부 에셋 오버라이드 없음 (건너뜀)');

  // [7] targetPlatform — 엔진의 광고 네트워크 enum이 실제로 읽는 값입니다
  //     (case "google" → GoogleAds). 목록에 없는 값은 Unknown이 됩니다.
  content = setTargetPlatform(content, 'google');
  step(7, 'targetPlatform → "google"');

  // [8] 이미지 base122 → base64 data URI (압축 번들은 base122 유지)
  const img = googleImagesToDataUri(content);
  content = img.content;
  step(8, img.count
    ? `이미지 ${img.count}개 base122 → base64 data URI`
    : '속성 base122 이미지 없음 (건너뜀)');
  if (img.leftover) step(8, `⚠ 짝 없는 data-src122 ${img.leftover}개 남음`, false);

  // 검증: 남은 mraid 호출과 외부 리소스 참조
  const mraidLeft = (content.match(/\bmraid\./g) || []).length;
  step('검증', mraidLeft ? `⚠ mraid 호출 ${mraidLeft}건 잔존` : 'mraid 호출 0건', mraidLeft === 0);

  const refs = findExternalRefs(content).filter(r => !/googlesyndication|googleusercontent/.test(r));
  if (refs.length) step('검증', `외부 참조 ${refs.length}건: ${refs.join(' · ')}`, true);

  return { content, log };
}

// ===========================================================================
// AppLovin 파이프라인
//
// 이 파이프라인은 PAW의 다른 네트워크와 코드를 공유하지 않습니다. 타사가
// Luna로 applovin 타겟을 직접 빌드한 파일을 자사 unityAds 빌드와 대조해
// 도출했습니다. applovin2(lunaAppID 26300)와 applovin3(36668)은 서로 다른
// 계정의 독립 빌드인데, 게임별 내용을 뺀 15개 <script> 블록의 md5가 일치합니다.
// 그중 원본에 없는 것은 브릿지 하나뿐입니다.
//
// AppLovin은 Google과 달리 스토어 이동을 가져가지 않습니다. 그래서 원본을
// 거의 그대로 둡니다 — mraid 생명주기도, 원본 CTA(mraid.open)도, 이미지
// base122도, 압축 번들도, 심지어 Luna가 내보내는 깨진 닫는 태그까지
// 정식 빌드가 손대지 않은 채로 둡니다. 실제로 달라지는 것은 아래 3가지뿐입니다.
// ===========================================================================

// 토큰을 품은 <script> 엘리먼트를 통째로 들어냅니다. 최소화된 Luna 빌드는
// 변수명이 매 빌드 바뀌므로 코드 모양이 아니라 API 이름에 앵커를 겁니다.
function cutScriptWithToken(content, token) {
  const tok = content.indexOf(token);
  if (tok < 0) return null;
  const start = content.lastIndexOf('<script', tok);
  if (start < 0) return null;
  const open = content.indexOf('>', start);
  if (open < 0 || open > tok) return null;
  // 토큰과 여는 태그 사이에 </script>가 있으면 그 <script>는 토큰을 감싼 태그가 아닙니다.
  if (content.slice(start, tok).indexOf('</script') >= 0) return null;
  const close = content.indexOf('</script>', tok);
  if (close < 0) return null;
  return content.slice(0, start) + content.slice(close + '</script>'.length);
}

function convertAppLovin(html) {
  const log = [];
  let content = html;
  const step = (n, label, ok = true) => log.push({ step: String(n), label, ok });

  // [1] targetPlatform — 엔진의 광고 네트워크 enum이 실제로 읽는 값입니다.
  //     정식 빌드 2/2가 "applovin"입니다. 수작업 변환본이 쓴 "aplvads"는
  //     목록에 없어 Unknown으로 떨어집니다.
  content = setTargetPlatform(content, 'applovin');
  step(1, 'targetPlatform → "applovin"');

  // [2] mraid 광고 메타 주입 블록 제거. getMraidAdData()는 Unity Ads 전용
  //     mraid 확장이라 AppLovin에는 없습니다. 남겨두면 분석 이벤트마다
  //     try/catch로 떨어지며 경고만 찍습니다. 정식 빌드 2/2에 없습니다.
  let cut = 0;
  for (const token of ['mraid.getMraidAdData', 'window.pi.originalLogEvent']) {
    const next = cutScriptWithToken(content, token);
    if (next) { content = next; cut++; }
  }
  step(2, cut ? `mraid 광고 메타 주입 블록 ${cut}개 제거` : 'mraid 메타 주입 블록 없음 (건너뜀)');

  // [3] 브릿지를 </body> 바로 앞에 삽입. 정식 빌드에서도 원본 CTA 블록
  //     바로 뒤에 놓입니다. 원본 CTA를 지우지 않는 이유는 등록 순서 때문입니다
  //     — 둘 다 luna:build에서 InstallFullGame을 대입하는데 브릿지가 뒤라
  //     최종 승자가 되고, 브릿지의 CTA가 원본과 같은 스토어 이동 로직을
  //     그대로 갖고 있어 동작이 바뀌지 않습니다.
  if (content.indexOf('APPLOVIN_ANALYTICS_EVENTS') >= 0) {
    step(3, 'AppLovin 브릿지: 이미 존재 (스킵)');
  } else if (content.indexOf('</body>') < 0) {
    step(3, '⚠ </body>를 찾지 못해 브릿지 미삽입', false);
  } else {
    const script = '<script>' + APPLOVIN_BRIDGE + '</script>';
    content = content.replace('</body>', () => script + '</body>');
    step(3, 'AppLovin 분석·생명주기 브릿지 삽입');
  }

  // 검증 — 브릿지가 원본에 의존하는 것들이 실제로 있는지.
  const deps = [
    ['Luna.Unity.Analytics.Applovin', 'Applovin 네임스페이스'],
    ['window.audioVolumeToggle', 'audioVolumeToggle'],
    ['luna:postrender', 'postrender 이벤트'],
  ];
  const missing = deps.filter(([t]) => content.indexOf(t) < 0).map(([, l]) => l);
  step('검증', missing.length
    ? `⚠ 브릿지 의존 항목 누락: ${missing.join(' · ')}`
    : '브릿지 의존 항목 모두 존재', missing.length === 0);

  return { content, log };
}

// ===========================================================================
// IronSource 파이프라인
//
// 이 파이프라인은 PAW의 다른 네트워크와 코드를 공유하지 않습니다. IronSource에
// 실제 송출된 크리에이티브를 수집해 자사 unityAds 빌드와 대조해 도출했습니다.
//
// 결론은 targetPlatform 한 줄입니다. 근거:
//
//  · 엔진에 DAPI/Nucleo 코드가 없습니다. 원본의 brotli 번들을 전부 푼 8.69MB
//    에서 dapi · DAPI · nucleo · Nucleo · apiType 이 모두 0건입니다. 환경정보
//    로그의 `apiType?"DAPI":"Nucleo":"MRAID"` 는 삼항식 **문자열**일 뿐이고
//    그 값을 읽는 코드는 어디에도 없습니다. packageConfig에 apiType 필드도
//    없습니다.
//
//  · HTML 스크립트가 targetPlatform을 읽는 곳은 두 군데뿐이고 둘 다
//    console.log 입니다 — AdNetworkPlatform 라벨과 ?startup 프로파일러 리포트.
//    동작을 바꾸는 분기가 없습니다.
//
//  · 신형 Luna의 pi 클래스에 injectIronSourceAdData(dapi.getAdData 폴백)가
//    있지만 targetPlatform이 아니라 pi 부트스트랩 인자(env.adNetwork)로
//    게이팅되고, mraid가 있으면 mraid를 먼저 씁니다. 우리 빌드는
//    window.pi.apply(window, []) 라 발화하지 않습니다.
//
//  · 실물 증거: IronSource에 송출된 크리에이티브 3개의 빌드 타겟이 각각
//    aplvads · unityads · moloco 였고 셋 다 mraid.open 으로 스토어를 엽니다.
//    ironsource 타겟으로 빌드된 것은 하나도 없었습니다. IronSource 컨테이너가
//    MRAID를 지원한다는 뜻이라, 원본의 mraid CTA와 생명주기가 그대로 동작합니다.
//
// 그래서 바꾸는 이유는 런타임 동작이 아니라 식별용 라벨입니다.
// ===========================================================================
function convertIronSource(html) {
  const content = setTargetPlatform(html, 'ironsource');
  const log = [{ step: '1', label: 'targetPlatform → "ironsource"', ok: true }];

  // 검증 — 원본이 기대대로 mraid 기반인지. 아니면 CTA가 죽습니다.
  const cta = content.indexOf('mraid.open') >= 0;
  log.push({ step: '검증', label: cta
    ? 'mraid CTA 확인 — IronSource 컨테이너가 MRAID를 지원하므로 그대로 동작'
    : '⚠ mraid.open을 찾지 못함 — CTA 경로 확인 필요', ok: cta });

  return { content, log };
}

// ===========================================================================
// Mintegral 파이프라인
//
// 이 파이프라인은 PAW의 다른 네트워크와 코드를 공유하지 않습니다. 타사가 Luna로
// mintegral 타겟을 직접 빌드한 파일 3개(Royal Match · XP Hero · Royal Kingdom)를
// 자사 unityAds 빌드와 대조해 도출했습니다.
//
// 세 파일이 갖고 있는 Mintegral SDK 본체 · MW_CONFIG · rayjump 로더 · 오디오
// 패치 · body mark="mobvista" 는 **만들지 않습니다.** 송출 시점에 Mintegral이
// 붙이는 것이기 때문입니다. 근거는 mintegral-blocks.js 주석에 있습니다.
//
// 우리가 만드는 것은 계약의 크리에이티브 쪽 절반입니다 — mraid 블록 3개를
// SDK와 대화하는 블록으로 1:1 교체하고, console 래퍼를 지우고, 인코딩을 맞춥니다.
// ===========================================================================

// 토큰을 품은 <script>의 본문만 갈아끼웁니다(태그와 속성은 유지). 최소화된
// Luna 빌드는 변수명이 매 빌드 바뀌므로 코드 모양이 아니라 API 이름에 앵커를
// 겁니다. 토큰과 여는 태그 사이에 </script>가 있으면 그 <script>는 토큰을 감싼
// 태그가 아니므로 null을 돌려줍니다.
function swapScriptBody(content, token, body) {
  const tok = content.indexOf(token);
  if (tok < 0) return null;
  const start = content.lastIndexOf('<script', tok);
  if (start < 0) return null;
  const open = content.indexOf('>', start);
  if (open < 0 || open > tok) return null;
  if (content.slice(start, tok).indexOf('</script') >= 0) return null;
  const close = content.indexOf('</script>', tok);
  if (close < 0) return null;
  return content.slice(0, open + 1) + body + content.slice(close);
}

function convertMintegral(html) {
  const log = [];
  let content = html;
  const step = (n, label, ok = true) => log.push({ step: String(n), label, ok });

  // [1] targetPlatform — 엔진의 광고 네트워크 enum이 실제로 읽는 값입니다.
  content = setTargetPlatform(content, 'mintegral');
  step(1, 'targetPlatform → "mintegral"');

  // [2] mraid 생명주기 → Mintegral 생명주기. 로드 후 곧바로 멈춘 채 대기하다가
  //     SDK가 gameStart()를 부르면 재개합니다.
  const life = swapScriptBody(content, 'mraid.isViewable()', MINTEGRAL_LIFECYCLE);
  if (life) { content = life; step(2, 'mraid 생명주기 → Mintegral 생명주기(gameReady/gameStart)'); }
  else step(2, 'mraid 생명주기 블록 없음 (건너뜀)');

  // [3] mraid CTA → install(). 스토어 이동을 호스트에 맡깁니다.
  const cta = swapScriptBody(content, 'Luna.Unity.Playable.InstallFullGame', MINTEGRAL_CTA);
  if (cta) { content = cta; step(3, 'CTA → window.install() · gameClose/gameEnd 연결'); }
  else step(3, '⚠ InstallFullGame 스크립트를 찾지 못함', false);

  // [3b] 분석 블록이 다시 대입한 InstallFullGame 도 install() 로 — 파일 위 주석.
  const ctaAlign = alignCtaAssignments(content); content = ctaAlign.content;
  if (ctaAlign.count) step('3b', CTA_ALIGN_LABEL(ctaAlign.count));

  // [4] mraid 광고 메타 주입 → MW_INIT.isRewardAds(). getMraidAdData는 Unity Ads
  //     전용 확장이라 Mintegral에는 없습니다.
  const ad = swapScriptBody(content, 'mraid.getMraidAdData', MINTEGRAL_AD_DATA);
  if (ad) { content = ad; step(4, 'pi.injectAdData → MW_INIT.isRewardAds()'); }
  else step(4, 'mraid 광고 메타 주입 블록 없음 (건너뜀)');

  // [5] Luna 원격 디버깅 래퍼 제거. console.log/warn/error 등 6개를 덮어써
  //     PlayTurbo 검수가 "Do not override console methods" 로 Code exception을
  //     냅니다. 정식 빌드 3/3에 이 블록이 없습니다. stats.js·spector URL과
  //     에셋 오버라이드는 정식 빌드 3/3에 남아 있으므로 건드리지 않습니다.
  const dt = removeDevtoolsBlock(content); content = dt.content;
  step(5, dt.removed ? 'console 재정의 래퍼(Luna 원격 디버깅) 제거' : 'console 재정의 래퍼 없음 (건너뜀)');

  // [6] 인코딩 — 이미지와 압축 번들 모두 base64. 정식 빌드 3/3이 그렇습니다
  //     (data-src122 0개, 압축 페이로드 전부 두 번째 인자 false).
  //     디코딩 드라이버 <script>는 그대로 둡니다. 정식 빌드도 그대로 두고,
  //     querySelectorAll("[data-src122]")가 빈 결과를 돌려주므로
  //     _decode122Promise가 즉시 resolve됩니다.
  let imgs = 0;
  content = content.replace(RX_SRC122_PAIR, (_m, srcA, mimeA, mimeB, srcB) => {
    const raw = srcA !== undefined ? srcA : srcB;
    const mime = unescapeAttrPayload(srcA !== undefined ? mimeA : mimeB);
    imgs++;
    return `src="data:${mime};base64,${b122ToBase64(unescapeAttrPayload(raw))}"`;
  });
  let bundles = 0;
  content = content.replace(RX_DECOMPRESS_B122, (_m, p1, payload, p3, p4) => {
    bundles++;
    return p1 + b122ToBase64(payload) + p3 + 'false' + p4;
  });
  step(6, (imgs || bundles)
    ? `base122 → base64: 이미지 ${imgs}개 · 압축 번들 ${bundles}개`
    : 'base122 페이로드 없음 (건너뜀)');

  const leftover = (content.match(/<img[^>]*data-src122/g) || []).length;
  if (leftover) step(6, `⚠ 짝 없는 data-src122 이미지 ${leftover}개 남음`, false);

  // 검증 — mraid가 남으면 Mintegral 컨테이너에서 죽는 경로가 생깁니다.
  const mraidLeft = (content.match(/\bmraid\./g) || []).length;
  step('검증', mraidLeft ? `⚠ mraid 호출 ${mraidLeft}건 잔존` : 'mraid 호출 0건', mraidLeft === 0);

  return { content, log };
}

// ===========================================================================
// Moloco 파이프라인
//
// 이 파이프라인은 PAW의 다른 네트워크와 코드를 공유하지 않습니다. Moloco에
// 송출된 플레이어블 3개를 자사 unityAds 빌드와 대조해 도출했습니다.
//
// 셋 중 둘(moloco.html · moloco2.html)이 targetPlatform "moloco"로 빌드된
// 정식 소재이고, 나머지 하나(moloco3.html)는 targetPlatform "unityads" —
// Moloco 전용 적응이 하나도 없는데도 그대로 송출됐습니다. 고유 블록 8개가
// 전부 게임별/Luna 버전 차이(LUCID · $environment · env-info · pi 클래스 ·
// 에셋 번들 · startGame)였습니다. 즉 Moloco 컨테이너는 MRAID를 지원하고
// 원본의 mraid 생명주기·CTA가 그대로 동작합니다.
//
// 그래서 원본을 거의 손대지 않습니다. mraid 블록도, 이미지 base122도,
// 압축 번들도, 프리로더도, Luna가 내보내는 깨진 닫는 태그까지 정식 빌드가
// 그대로 두므로 그대로 둡니다. 달라지는 것은 두 가지뿐입니다.
//
// 정식 빌드의 pi 부트스트랩 인자(appId · 서명 · 수집 엔드포인트)는 Luna
// 계정에서 발급되는 값이라 만들어낼 수 없습니다. 비워두면 분석 전송만
// 비활성이고 동작에는 영향이 없어 손대지 않습니다.
//
// 참조 빌드에 없는 적응이 하나 있습니다 — CTA의 FbPlayableAd.onCTAClick()
// 호출. Moloco 셀프서브 업로드 검사기는 .html 안에 그 호출 문자열이 있는지를
// 그대로 확인하고, 없으면 "Add the JavaScript function
// "FbPlayableAd.onCTAClick()" to your .html file" 로 업로드를 거부합니다
// (헬프센터 체크리스트: "FbPlayableAd.onCTAClick() 함수를 매개변수 없이
// 호출하도록 작성"). 참조 빌드 둘은 Luna가 호스팅해 익스체인지로 송출된
// 딜리버리라 이 검사기를 거친 적이 없고, 트램폴린이 onCTAClick 을 빈 함수로
// **정의**만 하지 호출은 하지 않습니다. 그래서 CTA 블록만 바꿉니다: 호스트가
// FbPlayableAd 를 주입했으면 그것을 부르고, 아니면 원본의 mraid.open 경로를
// 그대로 탑니다. 트램폴린이 살아나는 경우(#trampoline 주입)에는 트램폴린이
// 뒤에서 InstallFullGame 을 다시 덮어쓰므로 이 블록은 영향이 없습니다.
// ===========================================================================

// 원본 CTA 블록과 동일하되 FbPlayableAd.onCTAClick() 분기만 앞에 붙은 형태.
// 두 참조 빌드의 CTA 블록은 원본과 바이트 동일했으므로 원본 로직을 그대로
// 보존합니다(iOS/Android 링크 선택 · mraid.open · window.open 폴백).
const MOLOCO_CTA =
  'window.addEventListener("luna:build",(function(){Bridge.ready((function(){' +
  'Luna.Unity.Playable.InstallFullGame=function(n,i){' +
  'if(window.pi.logCta(),window.FbPlayableAd&&"function"==typeof FbPlayableAd.onCTAClick)' +
  'return void FbPlayableAd.onCTAClick();' +
  'n=n||window.$environment.packageConfig.iosLink,i=i||window.$environment.packageConfig.androidLink;' +
  'const o=/iphone|ipad|ipod|macintosh/i.test(window.navigator.userAgent.toLowerCase())?n:i;' +
  '"undefined"!=typeof mraid?mraid.open(o):(console.warn("Mraid is not defined"),window.open(o,"_blank"))' +
  '}}))}))';

function convertMoloco(html) {
  const log = [];
  let content = html;
  const step = (n, label, ok = true) => log.push({ step: String(n), label, ok });

  // [1] targetPlatform — 엔진의 광고 네트워크 enum이 실제로 읽는 값입니다.
  content = setTargetPlatform(content, 'moloco');
  step(1, 'targetPlatform → "moloco"');

  // [2] 트램폴린을 </body> 앞에 삽입. 정식 빌드에서도 mraid CTA 블록 바로
  //     뒤에 놓입니다 — 순서가 중요합니다. 트램폴린은 #trampoline 요소가
  //     없으면 첫 줄에서 TypeError로 죽는데, mraid CTA가 앞서 등록돼 있어
  //     CTA는 살아남습니다. 자세한 근거는 moloco-trampoline.js 주석 참조.
  if (content.indexOf('getElementById("trampoline")') >= 0) {
    step(2, 'Moloco 트램폴린: 이미 존재 (스킵)');
  } else if (content.indexOf('</body>') < 0) {
    step(2, '⚠ </body>를 찾지 못해 트램폴린 미삽입', false);
  } else {
    const script = '<script>' + MOLOCO_TRAMPOLINE + '</script>';
    content = content.replace('</body>', () => script + '</body>');
    step(2, 'Moloco 트램폴린 삽입 (추적 픽셀 · 탭 카운트 · CTA 재연결)');
  }

  // [3] CTA → FbPlayableAd.onCTAClick() 우선, mraid.open 폴백. Moloco 업로드
  //     검사기가 요구하는 호출 문자열을 넣는 단계입니다 — 위 주석 참조.
  if (/FbPlayableAd\s*\.\s*onCTAClick\s*\(\s*\)/.test(content)) {
    step(3, 'CTA: FbPlayableAd.onCTAClick() 호출 이미 존재 (스킵)');
  } else {
    const ctaBlock = metaFindScript(content, 'Luna.Unity.Playable.InstallFullGame');
    if (ctaBlock) {
      content = content.slice(0, ctaBlock.bodyStart) + MOLOCO_CTA + content.slice(ctaBlock.bodyEnd);
      step(3, 'CTA → FbPlayableAd.onCTAClick() 우선 · mraid.open 폴백');
    } else step(3, '⚠ InstallFullGame 스크립트를 찾지 못함 — Moloco 업로드가 거부됩니다', false);
    const ctaAlign = alignCtaAssignments(content); content = ctaAlign.content;
    if (ctaAlign.count) step('3b', CTA_ALIGN_LABEL(ctaAlign.count));
  }

  // 검증 — 트램폴린이 죽어도 CTA가 남으려면 mraid 경로가 살아 있어야 합니다.
  const cta = content.indexOf('mraid.open') >= 0;
  step('검증', cta
    ? 'mraid CTA 확인 — 트램폴린이 죽어도 스토어 이동 경로 유지'
    : '⚠ mraid.open을 찾지 못함 — CTA 경로 확인 필요', cta);

  return { content, log };
}

// ===========================================================================
// Meta (Facebook) 파이프라인
//
// 다른 네트워크와 달리 참조 빌드가 없습니다. Sensor Tower는 Meta 지면을
// 수집하지 않고, Meta 광고 라이브러리는 크리에이티브를 이미지·동영상으로만
// 분류해 플레이어블 HTML을 노출하지 않습니다(미디어 유형 필터에 항목 자체가
// 없습니다). 그래서 이 파이프라인만 **공식 사양에서 역산**했습니다:
// https://www.facebook.com/business/help/412951382532338
//
// 사양이 요구하는 것 —
//   · 모든 소재를 data URI로 담은 단일 HTML, 최대 5MB
//   · mraid.js 또는 유사 프레임워크 **없이** 작동
//   · 외부 네트워크 호출 불가 (XMLHttpRequest 명시적 금지), 다이내믹 소재 로딩 불가
//   · JavaScript 리디렉션 불가
//   · 스토어 이동은 FbPlayableAd.onCTAClick()
//   · 세로 방향, 여러 해상도에 반응형
//
// 핵심은 **덮어쓰기가 아니라 제거**입니다. mraid 블록을 남겨둔 채 CTA만 위에
// 얹으면 동작은 하지만, 사양이 금지한 mraid 호출과 스토어 URL을 직접 여는
// window.open이 파일에 그대로 남습니다. 심사가 파일을 읽는 방식이면 걸립니다.
// ===========================================================================

// 토큰을 품은 <script> 엘리먼트를 찾습니다. 최소화된 Luna 빌드는 변수명이 매
// 빌드 바뀌므로 코드 모양이 아니라 API 이름에 앵커를 겁니다.
function metaFindScript(content, token) {
  const tok = content.indexOf(token);
  if (tok < 0) return null;
  const start = content.lastIndexOf('<script', tok);
  if (start < 0) return null;
  const open = content.indexOf('>', start);
  if (open < 0 || open > tok) return null;
  if (content.slice(start, tok).indexOf('</script') >= 0) return null;
  const close = content.indexOf('</script>', tok);
  if (close < 0) return null;
  return { start, bodyStart: open + 1, bodyEnd: close, end: close + '</script>'.length };
}

// mraid 생명주기를 대신합니다. Meta는 노출 시점을 알려주는 API가 없으므로
// 곧바로 시작합니다.
const META_LIFECYCLE =
  'window.addEventListener("luna:build",(function(){window.pi.logLoaded(),' +
  'window.dispatchEvent(new Event("luna:start"))}))';

// 스토어 이동은 Meta SDK가 가져갑니다 — 크리에이티브는 눌렸다고 알리기만 하고
// 목적지는 Meta가 캠페인 설정에서 압니다. 폴백으로 window.open을 두지 않습니다:
// 사양이 JavaScript 리디렉션을 금지합니다.
//
// 호출은 방어적으로 감쌉니다. Meta 컨테이너에는 FbPlayableAd가 항상 주입되지만
// 그 밖에서는 없어서, 감싸지 않으면 CTA가 ReferenceError로 죽습니다. 업로드 전
// 로컬 확인이 그 상황이고, Mintegral 정식 빌드도 호스트 API를 같은 방식으로
// 부릅니다(window.install && window.install()).
const META_CTA =
  'window.addEventListener("luna:build",(function(){Bridge.ready((function(){' +
  'Luna.Unity.Playable.InstallFullGame=function(){window.pi.logCta(),' +
  'window.FbPlayableAd&&window.FbPlayableAd.onCTAClick()}}))}))';

// [3b] Luna's Meta logging is gated on the pi bootstrap's first argument:
//
//   window.pi = function (t, e, n, i, r, …) {
//     if (window.pi = new o(…), t === o.FACEBOOK_AD_NETWORK_NAME)
//       if ("undefined" != typeof FbPlayableAd) {
//         window.pi.logEvent = s;              // ← the FbPlayableAd logger
//         …
//         FbPlayableAd.initializeLogging(r + "/facebook", …)
//
// The source ships `window.pi.apply(window, [] || [])`, so `t` is undefined,
// the branch never runs, and logGameLoad / logLevelComplete / logButtonClick
// never fire — Meta receives no engagement telemetry at all, only the CTA.
//
// Passing the network name switches the logger in. The remaining arguments are
// Luna build credentials we do not have, and they only feed initializeLogging's
// query string — but `r` is concatenated as `r + "/facebook"`, so leaving it
// undefined would hand Meta the literal string "undefined/facebook". Empty
// strings make that "/facebook" instead, and buildId is filled from the build's
// own $environment where it is present. The logger is installed before
// initializeLogging runs, so the events survive even if Meta rejects the URL.
const RX_PI_BOOTSTRAP = /window\.pi\.apply\(\s*window\s*,\s*\[\s*\]\s*(?:\|\|\s*\[\s*\])?\s*\)/;

function metaWirePiTelemetry(content) {
  if (!RX_PI_BOOTSTRAP.test(content)) {
    // Non-empty argument list: the first element is already the ad network, and
    // overwriting a real credential set would do more harm than the missing
    // telemetry does.
    return { content, status: /window\.pi\.apply\(/.test(content) ? 'populated' : 'absent' };
  }
  const buildId = /buildId\s*:\s*(\d+)/.exec(content);
  const args = ['"facebook"', '""', buildId ? buildId[1] : '""', '""', '""'].join(',');
  return {
    content: content.replace(RX_PI_BOOTSTRAP, `window.pi.apply(window,[${args}])`),
    status: 'wired',
    buildId: buildId ? buildId[1] : null,
  };
}

function convertMeta(html) {
  const log = [];
  let content = html;
  const step = (n, label, ok = true) => log.push({ step: String(n), label, ok });

  // [1] targetPlatform — Luna 쪽 타깃 이름은 "facebook"입니다.
  content = setTargetPlatform(content, 'facebook');
  step(1, 'targetPlatform → "facebook"');

  // [2] mraid 생명주기 → 즉시 시작
  const life = metaFindScript(content, 'mraid.isViewable()');
  if (life) {
    content = content.slice(0, life.bodyStart) + META_LIFECYCLE + content.slice(life.bodyEnd);
    step(2, 'mraid 생명주기 → 즉시 luna:start');
  } else step(2, 'mraid 생명주기 블록 없음 (건너뜀)');

  // [3] CTA → FbPlayableAd.onCTAClick(). 원본의 mraid.open/window.open 경로가
  //     통째로 사라집니다.
  const cta = metaFindScript(content, 'Luna.Unity.Playable.InstallFullGame');
  if (cta) {
    content = content.slice(0, cta.bodyStart) + META_CTA + content.slice(cta.bodyEnd);
    step(3, 'CTA → FbPlayableAd.onCTAClick()');
  } else step(3, '⚠ InstallFullGame 스크립트를 찾지 못함', false);
  const ctaAlign = alignCtaAssignments(content); content = ctaAlign.content;
  if (ctaAlign.count) step('3a', CTA_ALIGN_LABEL(ctaAlign.count));

  // [3b] Meta 분석 로깅 연결 — 위 주석 참조.
  const pi = metaWirePiTelemetry(content);
  content = pi.content;
  step('3b', {
    wired:     () => `pi 부트스트랩 → "facebook" · FbPlayableAd 로깅 활성화${pi.buildId ? ` (buildId ${pi.buildId})` : ''}`,
    populated: () => 'pi 부트스트랩에 이미 인자가 있어 건드리지 않음',
    absent:    () => 'pi 부트스트랩을 찾지 못함 (건너뜀)',
  }[pi.status](), pi.status !== 'absent');

  // [4] mraid 광고 메타 주입 블록 제거. injectAdData와 그것을 부르는 logEvent
  //     래퍼는 짝이라 함께 지웁니다 — 하나만 지우면 남은 쪽이 던집니다.
  let cut = 0;
  for (const token of ['mraid.getMraidAdData', 'window.pi.originalLogEvent']) {
    const s = metaFindScript(content, token);
    if (s) { content = content.slice(0, s.start) + content.slice(s.end); cut++; }
  }
  step(4, cut ? `mraid 광고 메타 주입 블록 ${cut}개 제거` : 'mraid 메타 주입 블록 없음 (건너뜀)');

  // [5] 외부 네트워크 참조 제거 — 사양이 명시적으로 금지합니다.
  const dt = removeDevtoolsBlock(content); content = dt.content;
  const st = stripDevScriptUrls(content); content = st.content;
  const ao = stripAssetOverrides(content); content = ao.content;
  step(5, `외부 참조 제거 — devtools ${dt.removed}개 · 개발용 스크립트 ${st.count}개 · 에셋 오버라이드 ${ao.count}블록`);

  // [6] 인코딩 — 모든 소재를 base64 data URI로. 단일 파일 자기완결 요건입니다.
  let imgs = 0;
  content = content.replace(RX_SRC122_PAIR, (_m, srcA, mimeA, mimeB, srcB) => {
    const raw = srcA !== undefined ? srcA : srcB;
    const mime = unescapeAttrPayload(srcA !== undefined ? mimeA : mimeB);
    imgs++;
    return `src="data:${mime};base64,${b122ToBase64(unescapeAttrPayload(raw))}"`;
  });
  let bundles = 0;
  content = content.replace(RX_DECOMPRESS_B122, (_m, p1, payload, p3, p4) => {
    bundles++;
    return p1 + b122ToBase64(payload) + p3 + 'false' + p4;
  });
  step(6, (imgs || bundles)
    ? `base122 → base64: 이미지 ${imgs}개 · 압축 번들 ${bundles}개`
    : 'base122 페이로드 없음 (건너뜀)');

  // [7] 파일 끝 중복 닫는 태그 정리 — 단일 HTML 파일이어야 합니다.
  const tail = cleanTrailingTags(content);
  content = tail.content;
  step(7, tail.removed ? `파일 끝 중복 태그 ${tail.removed}개 정리` : '파일 끝 태그 정상');

  // --- 사양 검증 -----------------------------------------------------------
  const cnt = (t) => (content.split(t).length - 1);
  const mraidLeft = (content.match(/\bmraid\./g) || []).length;
  step('검증', mraidLeft ? `⚠ mraid 호출 ${mraidLeft}건 잔존 — 사양 위반` : 'mraid 호출 0건', mraidLeft === 0);

  const redirects = cnt('window.open');
  step('검증', redirects ? `⚠ window.open ${redirects}건 — JS 리디렉션 금지` : 'JS 리디렉션 0건', redirects === 0);

  const xhr = cnt('XMLHttpRequest');
  step('검증', xhr ? `⚠ XMLHttpRequest ${xhr}건 — 외부 호출 금지` : 'XMLHttpRequest 0건', xhr === 0);

  const refs = findExternalRefs(content);
  step('검증', refs.length ? `⚠ 외부 참조 ${refs.length}건: ${refs.join(' · ')}` : '외부 리소스 참조 0건', refs.length === 0);

  step('검증', cnt('FbPlayableAd.onCTAClick') ? 'FbPlayableAd.onCTAClick() 연결됨' : '⚠ CTA API 없음',
    cnt('FbPlayableAd.onCTAClick') > 0);

  // 사양은 세로 빌드를 요구하지만 packageConfig.orientation은 엔진도 HTML도
  // 읽지 않는 값이라, 바꿔도 가로 게임이 세로가 되지 않습니다. 알리기만 합니다.
  const or = (content.match(/\borientation:"([^"]*)"/) || [])[1] || '?';
  step('검증', or === 'portrait' ? '세로 방향 빌드'
    : `방향: "${or}" — 사양은 세로 빌드를 요구합니다. 원본 빌드 설정을 확인하세요`, or === 'portrait');

  const bytes = new TextEncoder().encode(content).length + 3;   // +3 = UTF-8 BOM
  const mib = (bytes / 1048576).toFixed(2);
  step('검증', bytes > META_SIZE_LIMIT ? `⚠ ${mib} MiB — 5MB 한도 초과` : `${mib} MiB — 5MB 한도 이내`,
    bytes <= META_SIZE_LIMIT);

  return { content, log };
}

// ===========================================================================
// Smadex 파이프라인
//
// Smadex가 배포한 Playables 규격 시트(DEBUG_SMADEX/spec.png)에서 도출했습니다.
// 광고주 제공(advertiser-provided) 소재에 요구하는 것 —
//
//   · 플레이어블 코드 전부를 담은 단일 HTML 파일
//   · UTF-8 문자 인코딩만 사용
//   · 5MB 미만
//   · **세로와 가로 양쪽 방향을 모두 지원** (Meta가 세로만 요구하는 것과 반대)
//   · 플레이어블 안의 **이미지**를 base64로 인코딩 — 로딩을 빠르게 하고 외부
//     리소스 접근 문제를 피하기 위함
//   · CTA 또는 엔드스크린에서 앱스토어로 이동 가능해야 함
//   · **smxTracking.redirect()** 를 파라미터 없이 포함 — 스토어 이동 수단
//   · 이벤트 3종:
//       "load"     광고가 정상 로드됐음을 알림. 반드시 있어야 함
//       "download" CTA 상호작용 또는 엔드스크린에서 발화
//       "complete" 플레이어블이 정상 종료됐음을 보장
//     그리고 **download와 complete가 함께 발화해서는 안 됨**
//   · **<script> 안에 한 줄 주석(//) 금지.** 블록 주석(/* */)과 HTML 주석
//     (<!-- -->)은 허용
//   · 스크립트와 에셋은 어느 기기에서든 접근 가능하도록 담을 것
//
// 규격이 base64를 **이미지에 한정해** 명시하므로 압축 번들은 base122 그대로
// 둡니다. base122도 인라인이라 자기완결 요건을 만족하고 유효한 UTF-8이며,
// 굳이 바꾸면 파일만 약 20만 바이트 커집니다.
// ===========================================================================

// 브릿지. 규격이 한 줄 주석을 금지하므로 블록 주석만 씁니다.
// download는 CTA에, complete는 게임 종료에 걸어 서로 다른 순간에만 발화합니다.
const SMADEX_BRIDGE = `<script>
        /* smadex bridge */
        (function () {
            function fire(name) {
                try {
                    if (typeof smxTracking !== "undefined" && smxTracking &&
                        typeof smxTracking[name] === "function") {
                        smxTracking[name]();
                        return true;
                    }
                } catch (e) {}
                return false;
            }

            function storeUrl() {
                var env    = window.$environment || {};
                var config = env.packageConfig   || {};
                return /iphone|ipad|ipod|macintosh/i.test(navigator.userAgent)
                    ? (config.iosLink || "") : (config.androidLink || "");
            }

            /* "load" — 광고가 정상 로드됐음 */
            window.addEventListener("luna:started", function () { fire("load"); });

            window.addEventListener("luna:build", function () {
                if (typeof Bridge === "undefined") return;
                Bridge.ready(function () {
                    /* "download" — CTA 상호작용. 이어서 redirect()가 스토어로 보냅니다.
                       호스트가 없는 단독 파일에서는 소재에 박힌 스토어 링크로 폴백합니다. */
                    Luna.Unity.Playable.InstallFullGame = function (n, i) {
                        fire("download");
                        if (fire("redirect")) return;
                        var url = n || i || storeUrl();
                        if (url) window.open(url, "_blank");
                    };
                    /* "complete" — 플레이어블 정상 종료. download와 트리거가 달라
                       두 이벤트가 같은 순간에 함께 발화하지 않습니다. */
                    var orig = Luna.Unity.LifeCycle.GameEnded;
                    Luna.Unity.LifeCycle.GameEnded = function () {
                        if (orig) orig();
                        fire("complete");
                    };
                });
            });
        })();
        </script>`;

const SMADEX_MARKER = '/* smadex bridge */';

// <script> 본문에서 한 줄 주석(//)을 찾습니다. 문자열·정규식·URL 안의 //는
// 세지 않도록 줄 시작(들여쓰기 허용) 기준으로만 봅니다.
function smadexLineComments(content) {
  let n = 0;
  for (const m of content.matchAll(/<script([^>]*)>([\s\S]*?)<\/script>/g)) {
    for (const line of m[2].split('\n')) if (/^\s*\/\//.test(line)) n++;
  }
  return n;
}

function convertSmadex(html) {
  const log = [];
  let content = html;
  const step = (n, label, ok = true) => log.push({ step: String(n), label, ok });

  // [1] targetPlatform. Luna에 대응하는 이름이 없어 키를 그대로 씁니다 —
  //     런타임이 이 값을 읽는 곳은 디버그 로그뿐이라 미지의 값도 안전합니다.
  content = setTargetPlatform(content, 'smadex');
  step(1, 'targetPlatform → "smadex"');

  // [2] 외부 리소스 참조 제거. 규격이 "어느 기기에서든 접근 가능하도록" 담으라고
  //     요구하고, base64 이미지의 이유로 "외부 리소스 접근 문제 회피"를 듭니다.
  const dt = removeDevtoolsBlock(content); content = dt.content;
  const st = stripDevScriptUrls(content); content = st.content;
  const ao = stripAssetOverrides(content); content = ao.content;
  step(2, `외부 참조 제거 — devtools ${dt.removed}개 · 개발용 스크립트 ${st.count}개 · 에셋 오버라이드 ${ao.count}블록`);

  // [3] 이미지 base64. 규격이 이미지만 명시하므로 압축 번들은 손대지 않습니다.
  let imgs = 0;
  content = content.replace(RX_SRC122_PAIR, (_m, srcA, mimeA, mimeB, srcB) => {
    const raw = srcA !== undefined ? srcA : srcB;
    const mime = unescapeAttrPayload(srcA !== undefined ? mimeA : mimeB);
    imgs++;
    return `src="data:${mime};base64,${b122ToBase64(unescapeAttrPayload(raw))}"`;
  });
  step(3, imgs ? `이미지 ${imgs}개 base122 → base64` : '속성 base122 이미지 없음 (건너뜀)');

  // [4] 브릿지를 </body> 앞에 삽입. 원본 CTA 블록보다 뒤라 InstallFullGame의
  //     최종 승자가 됩니다.
  if (content.indexOf(SMADEX_MARKER) >= 0) {
    step(4, 'Smadex 브릿지: 이미 존재 (스킵)');
  } else if (content.indexOf('</body>') < 0) {
    step(4, '⚠ </body>를 찾지 못해 브릿지 미삽입', false);
  } else {
    content = content.replace('</body>', () => SMADEX_BRIDGE + '\n    </body>');
    step(4, 'smxTracking 브릿지 삽입 (load · download · complete · redirect)');
  }

  // [5] 파일 끝 중복 닫는 태그 정리 — 단일 HTML 파일이어야 합니다.
  const tail = cleanTrailingTags(content);
  content = tail.content;
  step(5, tail.removed ? `파일 끝 중복 태그 ${tail.removed}개 정리` : '파일 끝 태그 정상');

  // --- 규격 검증 -----------------------------------------------------------
  const bytes = new TextEncoder().encode(content);

  step('검증', /^\s*<!DOCTYPE html>/i.test(content) ? '단일 HTML 파일' : '⚠ HTML 문서 형태가 아님',
    /^\s*<!DOCTYPE html>/i.test(content));

  // UTF-8 왕복이 깨지지 않는지 — 규격이 UTF-8 전용을 요구합니다.
  const utf8 = new TextDecoder('utf-8', { fatal: true });
  let utf8Ok = true;
  try { utf8.decode(bytes); } catch { utf8Ok = false; }
  step('검증', utf8Ok ? 'UTF-8 인코딩' : '⚠ UTF-8로 디코드되지 않음', utf8Ok);

  const mib = ((bytes.length + 3) / 1048576).toFixed(2);
  step('검증', bytes.length + 3 < SMADEX_SIZE_LIMIT ? `${mib} MiB — 5MB 미만`
    : `⚠ ${mib} MiB — 5MB 초과`, bytes.length + 3 < SMADEX_SIZE_LIMIT);

  // 규격은 세로·가로 **양쪽** 지원을 요구합니다. Meta와 반대입니다.
  const or = (content.match(/\borientation:"([^"]*)"/) || [])[1] || '?';
  const bothOr = or === 'unspecified';
  step('검증', bothOr ? '세로·가로 양쪽 지원 (orientation "unspecified")'
    : `⚠ orientation "${or}" — 규격은 세로·가로 양쪽 지원을 요구합니다`, bothOr);

  const left = (content.match(/<img[^>]*data-src122/g) || []).length;
  step('검증', left ? `⚠ base64로 바뀌지 않은 이미지 ${left}개` : '이미지 전부 base64', left === 0);

  const sx = content.indexOf('smxTracking.redirect') >= 0 || content.indexOf('"redirect"') >= 0;
  step('검증', sx ? 'smxTracking.redirect() 포함 (파라미터 없음)' : '⚠ redirect() 없음', sx);

  const ev = ['"load"', '"download"', '"complete"'].filter(e => content.includes(e));
  step('검증', ev.length === 3 ? '이벤트 3종 연결 — load · download · complete'
    : `⚠ 이벤트 누락: ${['"load"', '"download"', '"complete"'].filter(e => !ev.includes(e)).join(' ')}`,
    ev.length === 3);

  // download는 CTA, complete는 게임 종료 — 트리거가 달라야 함께 발화하지 않습니다.
  const bridgeAt = content.indexOf(SMADEX_MARKER);
  const bridge = bridgeAt < 0 ? '' : content.slice(bridgeAt, content.indexOf('</script>', bridgeAt));
  const split = /InstallFullGame[\s\S]*?fire\("download"\)/.test(bridge)
    && /GameEnded[\s\S]*?fire\("complete"\)/.test(bridge);
  step('검증', split ? 'download·complete 트리거 분리 (CTA / 게임 종료)'
    : '⚠ download와 complete가 같은 트리거에 걸려 있을 수 있음', split);

  const lc = smadexLineComments(content);
  step('검증', lc ? `⚠ <script> 안 한 줄 주석 ${lc}줄 — 규격 금지`
    : '<script> 안 한 줄 주석 0줄 (블록 주석만)', lc === 0);

  const refs = findExternalRefs(content);
  step('검증', refs.length ? `⚠ 외부 참조 ${refs.length}건: ${refs.join(' · ')}` : '외부 리소스 참조 0건',
    refs.length === 0);

  return { content, log };
}



// ---------------------------------------------------------------------------
// Main convert — returns { content, log: [{ step, label, ok }] }
// Mirrors :check_base64 (googleads only) + :do_convert step ordering.
// ---------------------------------------------------------------------------
export function convert(html, networkKey) {
  // 아래 일곱은 공통 파이프라인을 타지 않습니다 — 각자 근거에서 도출한
  // 단계만 적용합니다. 각 convertX() 위 주석을 보세요.
  if (networkKey === 'googleads')  return convertGoogleAds(html);
  if (networkKey === 'applovin')   return convertAppLovin(html);
  if (networkKey === 'ironsource') return convertIronSource(html);
  if (networkKey === 'mintegral')  return convertMintegral(html);
  if (networkKey === 'moloco')     return convertMoloco(html);
  if (networkKey === 'meta')       return convertMeta(html);
  if (networkKey === 'smadex')     return convertSmadex(html);

  // 남는 것은 unityads 하나입니다 — 원본이 이미 그 타겟이라 targetPlatform도
  // 바뀌지 않습니다. 아래는 .bat 시절 공통 정리 단계 그대로입니다.
  const log = [];
  let content = html;

  // [1] devtools block removal
  const dt = removeDevtoolsBlock(content);
  content = dt.content;
  log.push({ step: '1', label: `devtools 블록 제거: ${dt.removed}개`, ok: true });

  // [1b] 개발용 스크립트 주입 URL 무력화.
  const st = stripDevScriptUrls(content);
  content = st.content;
  log.push({ step: '1b', label: st.count
    ? `개발용 스크립트 참조 제거: ${st.hosts.join(' · ')}`
    : '개발용 스크립트 참조 없음 (건너뜀)', ok: true });

  // [1b] 원격 에셋 오버라이드 비우기. Unity도 에셋 인라인을 요구하고, 남겨두면
  //      런타임에 lunalabs CDN에서 텍스처를 다시 받습니다.
  const ao = stripAssetOverrides(content);
  content = ao.content;
  log.push({ step: '1b', label: ao.count
    ? `원격 에셋 오버라이드 ${ao.count}블록 비움`
    : '원격 에셋 오버라이드 없음 (건너뜀)', ok: true });

  // 남은 외부 참조는 제거하지 않고 알리기만 합니다 — 무엇이 걸릴지 모르는
  // 채로 잘라내면 크리에이티브를 깨뜨릴 수 있습니다.
  const refs = findExternalRefs(content);
  if (refs.length) {
    log.push({ step: '1b', label: `⚠ 외부 참조 ${refs.length}건 잔존: ${refs.join(' · ')}`, ok: false });
  }

  // [2] payload backtick 이스케이프.
  const bt = fixBackticks(content);
  content = bt.content;
  log.push({ step: '2', label: `payload backtick 이스케이프: ${bt.count}개`, ok: true });

  // [3] targetPlatform.
  content = setTargetPlatform(content, networkKey);
  log.push({ step: '3', label: `targetPlatform → "${networkKey}"`, ok: true });

  return { content, log };
}

// ---------------------------------------------------------------------------
// Encode content as UTF-8 with a leading BOM (EF BB BF), matching the .bat's
// PowerShell [System.Text.Encoding]::UTF8 default. Without this the web output
// is byte-identical to the .bat except for the missing 3-byte BOM.
// ---------------------------------------------------------------------------
export function utf8WithBom(content) {
  const body = new TextEncoder().encode(content);
  const out = new Uint8Array(body.length + 3);
  out[0] = 0xEF; out[1] = 0xBB; out[2] = 0xBF;
  out.set(body, 3);
  return out;
}

// ---------------------------------------------------------------------------
// ZIP builder — deflate-compressed entries, no external dependency.
// Uses the browser's CompressionStream('deflate-raw').
// entries: [{ name, data: Uint8Array }]
// ---------------------------------------------------------------------------
function crc32(bytes) {
  let crc = ~0;
  for (let i = 0; i < bytes.length; i++) {
    crc ^= bytes[i];
    for (let j = 0; j < 8; j++) {
      crc = (crc >>> 1) ^ (0xEDB88320 & -(crc & 1));
    }
  }
  return (~crc) >>> 0;
}

async function deflateRaw(bytes) {
  const cs = new CompressionStream('deflate-raw');
  const writer = cs.writable.getWriter();
  writer.write(bytes);
  writer.close();
  const ab = await new Response(cs.readable).arrayBuffer();
  return new Uint8Array(ab);
}

export async function makeZip(entries) {
  const enc = new TextEncoder();
  const fileParts = [];
  const centralParts = [];
  let offset = 0;
  let centralSize = 0;

  for (const e of entries) {
    const nameBytes = enc.encode(e.name);
    const crc = crc32(e.data);
    const comp = await deflateRaw(e.data);

    const lh = new DataView(new ArrayBuffer(30));
    lh.setUint32(0, 0x04034b50, true);
    lh.setUint16(4, 20, true);
    lh.setUint16(6, 0, true);
    lh.setUint16(8, 8, true);          // deflate
    lh.setUint16(10, 0, true);
    lh.setUint16(12, 0, true);
    lh.setUint32(14, crc, true);
    lh.setUint32(18, comp.length, true);
    lh.setUint32(22, e.data.length, true);
    lh.setUint16(26, nameBytes.length, true);
    lh.setUint16(28, 0, true);
    const lhBytes = new Uint8Array(lh.buffer);
    fileParts.push(lhBytes, nameBytes, comp);

    const cd = new DataView(new ArrayBuffer(46));
    cd.setUint32(0, 0x02014b50, true);
    cd.setUint16(4, 20, true);
    cd.setUint16(6, 20, true);
    cd.setUint16(8, 0, true);
    cd.setUint16(10, 8, true);
    cd.setUint16(12, 0, true);
    cd.setUint16(14, 0, true);
    cd.setUint32(16, crc, true);
    cd.setUint32(20, comp.length, true);
    cd.setUint32(24, e.data.length, true);
    cd.setUint16(28, nameBytes.length, true);
    cd.setUint16(30, 0, true);
    cd.setUint16(32, 0, true);
    cd.setUint16(34, 0, true);
    cd.setUint16(36, 0, true);
    cd.setUint32(38, 0, true);
    cd.setUint32(42, offset, true);
    const cdBytes = new Uint8Array(cd.buffer);
    centralParts.push(cdBytes, nameBytes);

    offset += lhBytes.length + nameBytes.length + comp.length;
    centralSize += cdBytes.length + nameBytes.length;
  }

  const eocd = new DataView(new ArrayBuffer(22));
  eocd.setUint32(0, 0x06054b50, true);
  eocd.setUint16(4, 0, true);
  eocd.setUint16(6, 0, true);
  eocd.setUint16(8, entries.length, true);
  eocd.setUint16(10, entries.length, true);
  eocd.setUint32(12, centralSize, true);
  eocd.setUint32(16, offset, true);
  eocd.setUint16(20, 0, true);

  return new Blob([...fileParts, ...centralParts, new Uint8Array(eocd.buffer)],
    { type: 'application/zip' });
}
